import { CalendarRange, CheckCheck, Loader2, ShieldAlert } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
	ACTIVE_NOTICES_PARAMS,
	useMarkNoticeAsRead,
	useNotices,
} from '@/api/queries/notices-queries'
import {
	AlertDialog,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/store'
import type { NoticeWithReadStatus } from '@/types/notice'
import { NoticeMedia } from './notice-media'
import { NOTICE_TONE } from './notice-tone'
import { NoticeWatermark } from './notice-watermark'
import { useContentProtection } from './use-content-protection'

/** Tempo mínimo de leitura antes de liberar a confirmação */
const MIN_READ_SECONDS = 5

const formatDay = (iso: string) => new Date(iso).toLocaleDateString('pt-BR')

/**
 * Modal que bloqueia a tela enquanto houver avisos de prioridade alta não lidos.
 * Não fecha por ESC nem clicando fora: o usuário precisa confirmar a leitura de cada aviso.
 */
export function NoticeBlockingDialog() {
	const { data } = useNotices(ACTIVE_NOTICES_PARAMS)

	// A fila é um retrato dos avisos: o refetch da lista gera nova URL SAS da mídia e
	// reiniciaria o vídeo em reprodução se usássemos os itens da query diretamente.
	const [queue, setQueue] = useState<NoticeWithReadStatus[]>([])
	const [acknowledgedCount, setAcknowledgedCount] = useState(0)
	const acknowledgedIds = useRef(new Set<string>())

	useEffect(() => {
		const incoming = (data?.items ?? []).filter(
			(notice) =>
				notice.priority === 'high' &&
				!notice.isRead &&
				!acknowledgedIds.current.has(notice.id),
		)
		if (incoming.length === 0) return

		setQueue((current) => {
			const queuedIds = new Set(current.map((notice) => notice.id))
			const fresh = incoming.filter((notice) => !queuedIds.has(notice.id))
			return fresh.length > 0 ? [...current, ...fresh] : current
		})
	}, [data?.items])

	const handleAcknowledged = (id: string) => {
		acknowledgedIds.current.add(id)
		const next = queue.filter((notice) => notice.id !== id)
		setQueue(next)
		// Zera a contagem ao esvaziar para que um novo lote comece em "1 de N"
		setAcknowledgedCount(next.length === 0 ? 0 : acknowledgedCount + 1)
	}

	const current = queue[0]
	const total = acknowledgedCount + queue.length

	return (
		<AlertDialog open={!!current}>
			{current && (
				<AlertDialogContent
					onEscapeKeyDown={(event) => event.preventDefault()}
					overlayClassName="bg-kaefer-dark-grey/70 backdrop-blur-sm"
					className={cn(
						'max-h-[calc(100dvh-1.5rem)] max-w-[calc(100%-1.5rem)] gap-0 overflow-hidden border-0 p-0 shadow-2xl data-[size=default]:sm:max-w-xl',
						current.media.length > 0 && 'data-[size=default]:sm:max-w-2xl',
					)}
				>
					<BlockingNoticeContent
						key={current.id}
						notice={current}
						position={acknowledgedCount + 1}
						total={total}
						onAcknowledged={handleAcknowledged}
					/>
				</AlertDialogContent>
			)}
		</AlertDialog>
	)
}

interface BlockingNoticeContentProps {
	notice: NoticeWithReadStatus
	position: number
	total: number
	onAcknowledged: (id: string) => void
}

function BlockingNoticeContent({
	notice,
	position,
	total,
	onAcknowledged,
}: BlockingNoticeContentProps) {
	const { t } = useTranslation()
	const user = useAuthStore((state) => state.user)
	const markNoticeAsRead = useMarkNoticeAsRead()
	const [secondsLeft, setSecondsLeft] = useState(MIN_READ_SECONDS)

	useContentProtection()

	useEffect(() => {
		if (secondsLeft <= 0) return
		const timer = setTimeout(() => setSecondsLeft((value) => value - 1), 1000)
		return () => clearTimeout(timer)
	}, [secondsLeft])

	const tone = NOTICE_TONE[notice.type]
	const Icon = tone.icon
	const watermark = user ? <NoticeWatermark badge={user.badge} /> : null
	const canConfirm = secondsLeft <= 0 && !markNoticeAsRead.isPending
	const period =
		notice.startsAt || notice.endsAt
			? `${notice.startsAt ? formatDay(notice.startsAt) : '—'} – ${notice.endsAt ? formatDay(notice.endsAt) : '—'}`
			: null

	const handleConfirm = () => {
		markNoticeAsRead.mutate(notice.id, {
			onSuccess: () => onAcknowledged(notice.id),
		})
	}

	return (
		<div className="flex max-h-[inherit] flex-col select-none [-webkit-touch-callout:none]">
			{/* Fita de sinalização */}
			<div
				aria-hidden="true"
				className={cn(
					'h-2.5 shrink-0 motion-safe:animate-[notice-stripes_1.2s_linear_infinite]',
					tone.stripes,
				)}
			/>

			{/* Cabeçalho */}
			<div className="relative shrink-0 overflow-hidden bg-kaefer-dark-grey px-6 pt-6 pb-5 text-white">
				<div
					aria-hidden="true"
					className={cn(
						'pointer-events-none absolute -top-16 -left-16 size-56 rounded-full opacity-30 blur-3xl',
						tone.solid,
					)}
				/>
				<div
					aria-hidden="true"
					className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.07)_1px,transparent_0)] bg-size-[18px_18px]"
				/>

				<div className="relative flex items-start gap-4">
					<span className="relative flex size-14 shrink-0 items-center justify-center">
						<span
							className={cn(
								'absolute inset-0 rounded-full motion-safe:animate-ping [animation-duration:2s]',
								tone.pulse,
							)}
						/>
						<span
							className={cn(
								'absolute -inset-1.5 rounded-full opacity-25',
								tone.solid,
							)}
						/>
						<span
							className={cn(
								'relative flex size-14 items-center justify-center rounded-full shadow-lg ring-4 ring-white/10',
								tone.iconWrap,
							)}
						>
							<Icon className="size-7" />
						</span>
					</span>

					<div className="min-w-0 flex-1 pt-0.5">
						<div className="mb-1.5 flex flex-wrap items-center gap-2">
							<span className="inline-flex items-center gap-1 font-bold text-[10px] text-white/70 uppercase tracking-[0.16em]">
								<ShieldAlert className="size-3.5" />
								{t('notices.blocking.eyebrow')}
							</span>
							<span
								className={cn(
									'rounded-full px-2 py-0.5 font-bold text-[10px] uppercase tracking-wider',
									tone.iconWrap,
								)}
							>
								{t(`administration.notificationsForm.type_${notice.type}`)}
							</span>
						</div>
						<AlertDialogTitle className="text-balance font-bold text-white text-xl leading-tight">
							{notice.title}
						</AlertDialogTitle>
						{period && (
							<p className="mt-2 inline-flex items-center gap-1.5 text-white/60 text-xs">
								<CalendarRange className="size-3.5" />
								{period}
							</p>
						)}
					</div>

					{total > 1 && (
						<span className="shrink-0 rounded-full bg-white/10 px-2.5 py-1 font-semibold text-[11px] text-white tabular-nums ring-1 ring-white/15">
							{t('notices.blocking.counter', { current: position, total })}
						</span>
					)}
				</div>
			</div>

			{/* Conteúdo */}
			<div className="min-h-0 flex-1 overflow-y-auto">
				<div className="relative px-6 py-5">
					<AlertDialogDescription className="whitespace-pre-wrap text-kaefer-dark-grey text-sm leading-relaxed">
						{notice.message}
					</AlertDialogDescription>
					{watermark}
				</div>
				{notice.media.length > 0 && (
					<div className="px-6 pb-5">
						<NoticeMedia media={notice.media} overlay={watermark} />
					</div>
				)}
			</div>

			{/* Rodapé */}
			<div className="flex shrink-0 flex-col-reverse items-stretch gap-3 border-t bg-kaefer-light-silver/30 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
				<div className="flex items-center justify-center gap-1.5 sm:justify-start">
					{total > 1 &&
						Array.from({ length: total }, (_, step) => (
							<span
								key={step}
								className={cn(
									'h-1.5 rounded-full transition-all duration-300',
									step + 1 < position && 'w-1.5 bg-kaefer-forest-green',
									step + 1 === position && cn('w-6', tone.solid),
									step + 1 > position && 'w-1.5 bg-kaefer-silver/50',
								)}
							/>
						))}
					{markNoticeAsRead.isError && (
						<span className="text-kaefer-red text-xs">
							{t('notices.blocking.error')}
						</span>
					)}
				</div>

				<Button
					type="button"
					disabled={!canConfirm}
					onClick={handleConfirm}
					className="relative h-11 w-full overflow-hidden bg-kaefer-dark-grey px-5 font-semibold text-white hover:bg-kaefer-medium-grey disabled:opacity-100 sm:h-10 sm:w-auto sm:min-w-52"
				>
					{/* Preenchimento que acompanha o tempo mínimo de leitura */}
					{secondsLeft > 0 && (
						<span
							aria-hidden="true"
							className="absolute inset-0 origin-left bg-white/15 motion-safe:animate-[notice-progress_linear_forwards]"
							style={{ animationDuration: `${MIN_READ_SECONDS}s` }}
						/>
					)}
					<span className="relative inline-flex items-center gap-2">
						{markNoticeAsRead.isPending ? (
							<Loader2 className="size-4 animate-spin" />
						) : (
							<CheckCheck className="size-4" />
						)}
						{secondsLeft > 0
							? t('notices.blocking.wait', { count: secondsLeft })
							: t('notices.blocking.acknowledge')}
					</span>
				</Button>
			</div>
		</div>
	)
}
